import { Link } from "react-router-dom";
import { Shield, Lock, FileText, Mail } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Who we are",
    body: [
      "Phoenix Academy of Dance & Arts (\"Phoenix\", \"we\", \"us\", \"our\") is an international dance academy with branches in Chennai (India), Sacramento (USA), and Kitchener (Canada). This Privacy Policy explains how we collect, use, store, and share information when you visit our website (phoenixdanceacademy.com) or interact with our enquiry, trial, and class-booking flows.",
      "If you have any questions about this policy or our handling of your data, contact us at phoenixacademy2506@gmail.com.",
    ],
  },
  {
    title: "2. Information we collect",
    body: [
      "We only collect information that you voluntarily provide through our website or messaging channels. This typically includes:",
    ],
    list: [
      "Identity & contact data — full name, email address, phone number, country, branch of interest.",
      "Enquiry data — the program you are interested in, age group of the student, trial preferences, and any messages you send us.",
      "Communication data — WhatsApp / call records when you reach our branch coordinators.",
      "Technical data — anonymous usage data such as browser type, device type, and pages visited (collected via standard analytics, if enabled).",
    ],
    after: [
      "We do not collect payment card data on this website. Class fees, when applicable, are processed offline at the studio or via a separate, secure payment partner.",
    ],
  },
  {
    title: "3. How we use your information",
    body: [
      "We use the information you provide strictly to operate Phoenix Dance Academy and to respond to you. Specifically, we use it to:",
    ],
    list: [
      "Respond to enquiries, schedule free trials, and confirm class registrations.",
      "Coordinate trials and classes through your nearest branch coordinator.",
      "Share important updates about your enrolled classes, schedule changes, or studio policies.",
      "Improve the website experience, fix issues, and understand which programs are most useful to our community.",
      "Send occasional updates about new batches, performances, or workshops — only if you opt in.",
    ],
  },
  {
    title: "4. Sharing & disclosure",
    body: [
      "We do not sell, rent, or trade your personal information. We share your data only with:",
    ],
    list: [
      "Our internal Phoenix branch coordinators (India / USA / Canada) who handle your enquiry.",
      "Trusted third-party tools we use to operate the site (e.g., form/messaging providers such as JotForm and WhatsApp). These providers process data under their own privacy terms.",
      "Authorities, when legally required (e.g., subpoena, court order, lawful regulatory request).",
    ],
  },
  {
    title: "5. WhatsApp & messaging",
    body: [
      "Our enquiry forms can open WhatsApp directly to a Phoenix coordinator. When you choose to send a message via WhatsApp, the message and your phone number are visible to our coordinator and to Meta / WhatsApp under their own privacy policy. We retain those conversations only for as long as needed to follow up on your enquiry or class.",
    ],
  },
  {
    title: "6. Cookies & analytics",
    body: [
      "Our site uses minimal, essential cookies to keep the site working correctly. If we enable analytics tools (such as Google Analytics) we will use them only to understand aggregate, anonymised traffic — never to identify individuals. You can disable cookies in your browser at any time.",
    ],
  },
  {
    title: "7. Data retention",
    body: [
      "We retain your personal data only for as long as is necessary for the purpose for which it was collected, or to comply with legal, accounting, or reporting requirements. Enquiry data is typically kept for up to 24 months after your last interaction with us, unless you ask us to delete it sooner.",
    ],
  },
  {
    title: "8. Your rights",
    body: [
      "You have the right to:",
    ],
    list: [
      "Request access to the personal data we hold about you.",
      "Request correction of inaccurate or incomplete data.",
      "Request deletion of your personal data (subject to legal retention requirements).",
      "Withdraw consent for marketing communication at any time.",
      "File a complaint with your local data protection authority.",
    ],
    after: [
      "To exercise any of these rights, email us at phoenixacademy2506@gmail.com with the subject line \"Privacy Request\".",
    ],
  },
  {
    title: "9. Children's privacy",
    body: [
      "We work with students of all ages, including children. Where a student is under 18 (or the age of majority in your country), enrollment, trial bookings, and personal data must be provided by a parent or legal guardian. We will only collect and use a minor's data with parental consent and for the purpose of running their classes safely.",
    ],
  },
  {
    title: "10. Security",
    body: [
      "We take reasonable technical and organisational measures to protect your information from unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    title: "11. International transfers",
    body: [
      "Because Phoenix operates branches in India, the USA, and Canada, your information may be transferred between these jurisdictions when you choose a specific branch. We rely on contractual safeguards and the privacy frameworks of each country to protect your data during such transfers.",
    ],
  },
  {
    title: "12. Changes to this policy",
    body: [
      "We may update this Privacy Policy from time to time to reflect changes in law, our practices, or the services we offer. The latest version will always be available on this page with an updated \"Last revised\" date.",
    ],
  },
  {
    title: "13. Contact us",
    body: [
      "Phoenix Dance Academy",
      "Main Branch — No.267, MKB Nagar, Vyasarpadi, Chennai 600039, India",
      "Email: phoenixacademy2506@gmail.com",
      "Phone / WhatsApp: +91 82482 14953",
    ],
  },
];

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0a05] via-black to-black" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-20">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-4 h-4 text-[#ff4500]" />
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700]">◆ Legal</p>
          </div>
          <h1 className="font-display text-5xl lg:text-7xl text-white leading-[0.95]">
            Privacy <span className="phoenix-text">Policy.</span>
          </h1>
          <p className="mt-6 text-base lg:text-lg text-white/65 max-w-2xl leading-relaxed">
            How Phoenix Dance Academy collects, uses, and protects the information you share with us across India, USA, and Canada.
          </p>
          <p className="mt-8 text-xs uppercase tracking-[0.25em] text-white/40">
            Last revised · February 2026
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Sticky TOC */}
          <aside className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-28">
              <p className="text-xs uppercase tracking-[0.25em] text-[#ffb700] mb-4">◆ On this page</p>
              <ul className="space-y-2 text-xs text-white/55">
                {SECTIONS.map((s, i) => (
                  <li key={i}>
                    <a href={`#sec-${i}`} className="hover:text-[#ff4500] transition-colors leading-relaxed block">
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <article className="lg:col-span-9 space-y-12">
            {SECTIONS.map((s, i) => (
              <section key={i} id={`sec-${i}`} data-testid={`privacy-sec-${i}`}>
                <h2 className="font-display text-2xl lg:text-3xl text-white mb-5 leading-tight">
                  {s.title}
                </h2>
                {s.body?.map((p, j) => (
                  <p key={j} className="text-sm lg:text-base text-white/70 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}
                {s.list && (
                  <ul className="space-y-2 mb-4 list-none">
                    {s.list.map((it, j) => (
                      <li key={j} className="text-sm lg:text-base text-white/70 leading-relaxed flex gap-3">
                        <span className="text-[#ff4500] mt-1.5 shrink-0">◆</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.after?.map((p, j) => (
                  <p key={j} className="text-sm lg:text-base text-white/70 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}
              </section>
            ))}

            {/* Bottom CTA */}
            <div className="border border-white/10 bg-[#0a0a0a] p-8 mt-16">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#ff4500]/15 border border-[#ff4500]/30 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-[#ff4500]" />
                </div>
                <div>
                  <p className="font-display text-xl text-white mb-2">Need something specific?</p>
                  <p className="text-sm text-white/65 leading-relaxed mb-4">
                    Email us at <a href="mailto:phoenixacademy2506@gmail.com" className="text-[#ffb700] hover:text-[#ff4500] transition-colors">phoenixacademy2506@gmail.com</a> for any privacy-related request — we respond within 24 hours on weekdays.
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <Link to="/contact" className="border border-white/15 text-white px-4 h-9 inline-flex items-center uppercase tracking-wider hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors">
                      Contact us
                    </Link>
                    <Link to="/terms" className="border border-white/15 text-white px-4 h-9 inline-flex items-center uppercase tracking-wider hover:bg-[#ffb700] hover:text-black hover:border-[#ffb700] transition-colors">
                      <FileText className="w-3.5 h-3.5 mr-2" /> Terms of Use
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
